import React from "react";
import { Modal } from "react-bootstrap";
import Back from "assets/images/svg/back_blue.svg";
import MediaDropperComponent from "components/media-dropper/media-dropper.component";
import { useQuery, useQueryClient } from "react-query";
import { client } from "helpers/client";
import { PriorityStatus, Subject, TicketTopicEnum } from "pages/tickets/ticket.service";
import { Form, InputField, SelectField, Button } from "components/form";
import { useAuthContext } from "store/auth";
import { json, toBase64 } from "helpers/data";
import Input from "components/input-fields/input-fields";
import { errorClass } from "components/classes";
import Select from "react-select";
import { useStatus } from "hooks/use-status";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

interface NewTicketDialogProps {
  show: boolean;
  onHide: () => void;
  isAddingTicket?: boolean;
}

const NewTicketDialog = ({ show, onHide, isAddingTicket = false }: NewTicketDialogProps) => {
  const [{ user }] = useAuthContext();
  const queryClient = useQueryClient();
  const { state }: any = useLocation();
  const { t } = useTranslation("tickets");
  const { load, isLoading, done, stop } = useStatus();

  const { data: products = [] } = useQuery("products-lookup", async () => {
    const { response } = await client<any[]>("/customerProduct/getCustomerProductsLookup");
    if (response) return response;
  });
  const { data: billings = [] } = useQuery("invoices-lookup", async () => {
    const { response } = await client<any[]>("/invoice/getTicketInvoicesLookup");
    if (response) return response;
  });
  const { data: subjects } = useQuery("/Ticket/GetSubjectsLookup", async () => {
    const { error, response } = await client<string>("/Ticket/GetSubjectsLookup");
    if (error) throw new Error("server error");
    if (response) return JSON.parse(response) as Subject[];
  });

  async function handleCreateTicket(data: any) {
    load();
    const ticketDto = {
      subjectId: data.subject,
      title: data.title,
      topic: data.topic,
      productNumber: data.productId,
      description: data.description,
      invoiceId: data.invoiceId,
      senderName: user.Name,
      hasAttachment: !!data.attachment,
      attachment: {
        fileName: data.attachment?.name,
        mimeType: data.attachment?.type,
        fileBase64: data.attachment ? await toBase64(data.attachment) : undefined
      }
    };
    const { response } = await client("/ticket/createTicket", { data: ticketDto });
    if (response) {
      done();
      onHide();
      return queryClient.invalidateQueries(["tickets"]);
    }
    stop();
  }

  return (
    <div>
      <Modal
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        show={show}
        onHide={onHide}
        className="all-modal changeNumber"
      >
        <Modal.Body>
          <div className="header">
            <img src={Back} alt="back" onClick={onHide} />
            <h1>{t("new-ticket")}</h1>
          </div>
          <Form
            onSubmit={handleCreateTicket}
            className="row"
            defaultValues={{
              topic: isAddingTicket ? TicketTopicEnum.Billing : "",
              invoiceId: state?.invoice?.id
            }}
          >
            {({ register, watch, setValue, formState: { errors } }) => {
              const topicId = watch("topic");
              const attachment: File | undefined = watch("attachment");
              const subject: Subject | undefined = subjects?.find((s) => s.id === watch("subject"));
              const descriptionCount = watch("description")?.length ?? 0;
              return (
                <>
                  <div className="col-12">
                    <InputField id="title" label={t("title")} rules={{ required: "errors:required" }} />
                    <label
                      className={!!errors["description"] ? "div-textarea error textarea-height" : "div-textarea textarea-height"}
                    >
                      <div className="header">
                        <span className="title">{t("description")}</span>
                        <span className="count">{descriptionCount}/200</span>
                      </div>
                      <textarea {...register("description", { required: "errors:required" })} maxLength={200} />
                    </label>
                    {/* @ts-ignore */}
                    <span className={errorClass}>{t(errors["description"]?.message ?? "")}</span>
                    <SelectField
                      disabled={isAddingTicket}
                      id="topic"
                      label={t("topic")}
                      options={[
                        {
                          id: TicketTopicEnum.TechnicalSupport,
                          label: t("technical-support")
                        },
                        { id: TicketTopicEnum.Billing, label: t("billing") }
                      ]}
                    />

                    {topicId === TicketTopicEnum.TechnicalSupport && (
                      <>
                        <Select
                          className="selectSearch"
                          getOptionValue={(p) => p.crmId}
                          getOptionLabel={(p) => json(p.versionName)}
                          placeholder={t("search-here")}
                          options={products}
                          {...register("productId", { required: "errors:required" })}
                          onChange={(e) => setValue("productId", e.crmId.toString(), { shouldValidate: true })}
                        />
                        {/* @ts-ignore */}
                        <span className={errorClass}>{t(errors["productId"]?.message ?? "")}</span>
                        <SelectField
                          id="subject"
                          label={t("subject")}
                          options={subjects ?? []}
                          labelField="name"
                          valueField="id"
                          rules={{ required: "errors:required" }}
                        />
                        <Input
                          id="priority"
                          type="text"
                          label={t("priority")}
                          readonly
                          value={subject && PriorityStatus[subject.PriorityId]}
                        />
                        <MediaDropperComponent
                          label={t("drop-here")}
                          file={attachment}
                          onChange={(file) => setValue("attachment", file)}
                        />
                      </>
                    )}

                    {topicId === TicketTopicEnum.Billing && (
                      <Select
                          defaultInputValue={state?.invoice?.serial}
                        className="selectSearch"
                        getOptionValue={(p) => p.id}
                        getOptionLabel={(p) => json(p.serial)}
                        placeholder={t("search-invoice-here")}
                        options={billings}
                        {...register("invoiceId", {
                          required: "errors:required",
                          onChange: console.log
                        })}
                        onChange={(e) => setValue("invoiceId", e.id, { shouldValidate: true })}
                      />
                    )}
                  </div>
                  <div className="col-12">
                    <Button isLoading={isLoading} className="btn-blue">
                      {t("submit-request")}
                    </Button>
                  </div>
                </>
              );
            }}
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default NewTicketDialog;
